import { fabric } from 'fabric';
import type { Point } from 'fabric/fabric-impl';
import type { Constructor } from '../types';
import type ControlBase from '../fabric/ControlBase';

// In fabric, controls are only drawn on the active object, and events
// only occur on controls for the active object. The idea of controls
// is useful for other interactive object decorations, though. For
// voting, for example, we want to draw a voting control on every
// votable object, regardless of whether the object is selected.
//
// Additionally, controls cannot be rendered outside the active object.
// Persistent controls can.
//
// Persistent controls act exactly like controls, to the point that a
// ControlBase should reasonably be able to be used as a fabric
// control or a persistent control without changes. That's the goal,
// at least.
//
// To add persistent controls to an object, create a ControlBase and
// add it to the object's persistentControls property under an
// arbitrary key. See ShapeNote's voting controls for an example.
interface ShapePersistentControlBehaviors {
  __hasPersistentControlBehaviors: boolean;
  persistentControls: { [key: string]: ControlBase };
  pCoords: { [key: string]: Point };
  findTargetPersistentControl(
    pointer: { x: number; y: number },
    forTouch: boolean
  ): ControlBase | undefined;
  hasPersistentControls: boolean;
  renderPersistentControls: (ctx: CanvasRenderingContext2D) => void;
}

export interface ShapePersistentControlBehaviorsConstructor {
  new (...args: any[]): ShapePersistentControlBehaviors;
  persistentControls: { [key: string]: ControlBase };
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function ShapePersistentControlBehaviors(
  Base: Constructor<fabric.Object>
): any {
  const WithShapePersistentControlBehaviors: ShapePersistentControlBehaviorsConstructor = class extends Base {
    __hasPersistentControlBehaviors = true;
    persistentControls: { [key: string]: ControlBase } = {};
    pCoords: { [key: string]: Point } = {};

    static persistentControls: { [key: string]: ControlBase } = {};

    constructor(...args: any[]) {
      super(...args);
      this.persistentControls =
        WithShapePersistentControlBehaviors.persistentControls;
    }

    get hasPersistentControls(): boolean {
      return (
        this.persistentControls &&
        Object.keys(this.persistentControls).length > 0
      );
    }

    forEachPersistentControl(
      fn: (
        control: ControlBase,
        key: string,
        fabricObject: fabric.Object
      ) => void
    ) {
      if (!this.hasPersistentControls) return;

      for (const key in this.persistentControls) {
        fn(this.persistentControls[key], key, this);
      }
    }

    setCoords(skipCorners?: boolean) {
      super.setCoords(skipCorners);
      if (!skipCorners) {
        this.pCoords = this.calcPersistentControlCoords();
      }
      return this;
    }

    calcPersistentControlCoords(): { [key: string]: Point } {
      if (!this.hasPersistentControls) return {};

      const multiplyMatrices = fabric.util.multiplyTransformMatrices;
      const rotateMatrix = this._calcRotateMatrix();
      const translateMatrix = this._calcTranslateMatrix();
      const vpt = this.getViewportTransform();
      const startMatrix = multiplyMatrices(vpt, translateMatrix);
      const semiFinalMatrix = multiplyMatrices(startMatrix, rotateMatrix);
      const finalMatrix = multiplyMatrices(semiFinalMatrix, [
        1 / vpt[0],
        0,
        0,
        1 / vpt[3],
        0,
        0,
      ]);
      const dim = this._calculateCurrentDimensions();
      const coords = {};

      this.forEachPersistentControl((control, key, fabricObject) => {
        coords[key] = control.positionHandler(
          dim,
          finalMatrix,
          fabricObject,
          control
        );
      });
      return coords;
    }

    findTargetPersistentControl(pointer: {
      x: number;
      y: number;
    }): ControlBase | undefined {
      if (!this.hasPersistentControls) return;

      // Doesn't use forEachPersistentControl to support early return
      for (const key in this.persistentControls) {
        const control = this.persistentControls[key];

        if (!control.getVisibility(this, key)) {
          continue;
        }

        if (control.containsPoint?.(pointer, this)) {
          return control;
        }

        const coords = this.pCoords[key];
        if (!coords) return;

        const width = control.sizeX ?? 0;
        const height = control.sizeY ?? 0;
        const dims = {
          x: coords.x - width / 2,
          y: coords.y - height / 2,
          width,
          height,
        };

        if (dims.width === undefined || dims.height === undefined) {
          continue;
        }

        if (
          pointer.x >= dims.x &&
          pointer.x <= dims.x + dims.width &&
          pointer.y >= dims.y &&
          pointer.y <= dims.y + dims.height
        ) {
          return control;
        }
      }
    }

    renderPersistentControls(ctx: CanvasRenderingContext2D): void {
      const visibleControls: Array<[ControlBase, string]> = [];

      this.forEachPersistentControl((control, key) => {
        if (control.getVisibility(this, key)) {
          visibleControls.push([control, key]);
        }
      });

      if (!visibleControls.length) return;

      this.setCoords();

      ctx.save();

      for (const [control, key] of visibleControls) {
        const p = this.pCoords[key];
        control.render(ctx, p.x, p.y, {}, this);
      }

      ctx.restore();
    }
  };

  return WithShapePersistentControlBehaviors;
}

export default ShapePersistentControlBehaviors;
